import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useLangue, useTextes } from '../LangueContexte'
import { useVille } from '../VilleContexte'
import { rechercher, type Vignette as Annonce } from '../apiRecherche'
import { Vignette, VignetteSquelette } from './Vignette'
import { Bouton, EtatVide, Icone, LienBouton, type NomIcone } from '../ui'
import { estErreurApi, interpoler, phraseDErreur } from '../i18n'

/**
 * Les états d'erreur — revus le 16/09/2026 sur `docs/ui/pwa/introuvable.html`.
 *
 * **Une seule anatomie, cinq situations.** Chaque état est l'état vide du §5.3 — un pictogramme,
 * un titre qui nomme ce qui manque, une phrase qui l'explique, deux sorties au plus — suivi, quand
 * la situation s'y prête, d'une *suite* : ce qu'on peut faire d'utile ici plutôt que partir. Une
 * annonce disparue est remplacée par d'autres de la même ville ; un bail introuvable affiche les
 * siens ; une route inconnue montre les dernières annonces. Le service indisponible n'a pas de
 * suite : rien ne se charge, et proposer des annonces qui n'arriveraient pas serait un mensonge.
 *
 * **Le statut HTTP décide, pas le code.** L'API répond 404 à un bail dont on n'est pas partie et à
 * une annonce non publiée — un 403 confirmerait l'existence de la ressource. L'écran adopte la
 * même règle : « introuvable » couvre les deux, et la phrase le dit.
 */

/** Ce qui manque. Chaque valeur a son titre, sa phrase, son pictogramme et ses sorties. */
export type ObjetIntrouvable = 'page' | 'annonce' | 'bail' | 'conversation' | 'vote' | 'lien'

/**
 * L'état d'une fiche qui n'a pas pu se charger, d'après la cause.
 *
 * 404 → introuvable (avec la `suite` de l'appelant), 403 → pas ouvert, 401 → session expirée,
 * tout le reste → le service ne répond pas, avec « Réessayer » qui relance l'appel et non la page.
 */
export function EcranErreur({
  cause,
  objet,
  onReessayer,
  suite,
}: {
  cause: unknown
  objet: ObjetIntrouvable
  onReessayer?: () => void
  suite?: ReactNode
}) {
  const statut = estErreurApi(cause) ? cause.statut : 0

  if (statut === 404) return <Introuvable objet={objet} suite={suite} />
  if (statut === 403) return <AccesRefuse />
  if (statut === 401) return <SessionExpiree />

  return <ServiceIndisponible cause={cause} onReessayer={onReessayer} />
}

const ICONES: Record<ObjetIntrouvable, NomIcone> = {
  page: 'carte',
  annonce: 'maison',
  bail: 'cle',
  conversation: 'message',
  vote: 'groupe',
  lien: 'cle',
}

/** Ce qui n'existe pas, ou plus — ou qu'on n'a pas le droit de voir, ce qui revient au même ici. */
export function Introuvable({ objet, suite }: { objet: ObjetIntrouvable; suite?: ReactNode }) {
  const t = useTextes()

  const titres: Record<ObjetIntrouvable, [string, string]> = {
    page: [t.pageIntrouvable, t.introuvableAide],
    annonce: [t.erreurAnnonceTitre, t.erreurAnnonceAide],
    bail: [t.erreurBailTitre, t.erreurBailAide],
    conversation: [t.erreurConversationTitre, t.erreurConversationAide],
    vote: [t.erreurVoteTitre, t.erreurVoteAide],
    lien: [t.erreurLienTitre, t.erreurLienAide],
  }
  const [titre, aide] = titres[objet]

  return (
    <section className="erreur">
      <EtatVide icone={ICONES[objet]} titre={titre} action={<Sorties objet={objet} />}>
        {aide}
      </EtatVide>
      {suite}
    </section>
  )
}

/** Deux sorties au plus, et la première est celle qu'on prendrait. */
function Sorties({ objet }: { objet: ObjetIntrouvable }) {
  const t = useTextes()

  switch (objet) {
    case 'bail':
      return (
        <div className="rangee rangee--serree">
          <LienBouton to="/mes-baux">{t.mesBaux}</LienBouton>
          <LienBouton to="/foyer" apparence="secondaire">
            {t.foyer}
          </LienBouton>
        </div>
      )
    case 'conversation':
      return (
        <div className="rangee rangee--serree">
          <LienBouton to="/messages">{t.erreurConversationSortie}</LienBouton>
          <LienBouton to="/recherche" apparence="secondaire">
            {t.revenirRecherche}
          </LienBouton>
        </div>
      )
    case 'vote':
      return (
        <div className="rangee rangee--serree">
          <LienBouton to="/avis-du-foyer">{t.voteFoyer}</LienBouton>
          <LienBouton to="/foyer" apparence="secondaire">
            {t.foyer}
          </LienBouton>
        </div>
      )
    case 'lien':
      return (
        <div className="rangee rangee--serree">
          <LienBouton to="/mot-de-passe-oublie">{t.erreurLienSortie}</LienBouton>
          <LienBouton to="/connexion" apparence="secondaire">
            {t.seConnecter}
          </LienBouton>
        </div>
      )
    default:
      return (
        <div className="rangee rangee--serree">
          <LienBouton to="/recherche">{t.revenirRecherche}</LienBouton>
          <LienBouton to="/favoris" apparence="secondaire">
            {t.introuvableFavoris}
          </LienBouton>
        </div>
      )
  }
}

/**
 * La suite d'une annonce ou d'une page qui manque : les dernières annonces de la ville courante.
 *
 * Trois, pas une page : c'est une invitation, pas une recherche. Rien ne s'affiche tant qu'elles
 * n'arrivent pas — un squelette — et rien non plus si l'appel échoue : l'état d'erreur est déjà
 * là, un second bandeau n'apprendrait rien.
 */
export function AutresAnnonces({ titre, toutes }: { titre: string; toutes?: string }) {
  const ville = useVille()
  const code = ville.courante?.code ?? ville.defaut
  const [annonces, setAnnonces] = useState<Annonce[] | null>(null)

  useEffect(() => {
    if (code === null) return

    let vivant = true

    void rechercher({ cityCode: code }, 'Newest', 1, 3)
      .then((page) => {
        if (vivant) setAnnonces(page.items)
      })
      .catch(() => {
        if (vivant) setAnnonces([])
      })

    return () => {
      vivant = false
    }
  }, [code])

  if (code === null || (annonces !== null && annonces.length === 0)) {
    return null
  }

  const nom = ville.libelle(code)

  return (
    <div className="erreur__suite">
      <h2>{interpoler(titre, { ville: nom })}</h2>
      <ul className="grille-annonces">
        {annonces === null
          ? Array.from({ length: 3 }, (_, rang) => <VignetteSquelette key={rang} />)
          : annonces.map((annonce) => <Vignette key={annonce.id} annonce={annonce} />)}
      </ul>
      {toutes !== undefined && annonces !== null && (
        <p>
          <LienBouton to={`/recherche?city=${encodeURIComponent(code)}`} apparence="discret">
            {interpoler(toutes, { ville: nom })}
            <Icone nom="chevron" taille="sm" miroir />
          </LienBouton>
        </p>
      )}
    </div>
  )
}

/** 403 : la ressource existe, elle n'est pas à nous. Rare — l'API préfère 404 — mais possible. */
export function AccesRefuse() {
  const t = useTextes()

  return (
    <section className="erreur">
      <EtatVide
        icone="bouclier"
        titre={t.erreurAccesTitre}
        action={
          <div className="rangee rangee--serree">
            <LienBouton to="/recherche">{t.revenirRecherche}</LienBouton>
            <LienBouton to="/profil" apparence="secondaire">
              {t.monProfil}
            </LienBouton>
          </div>
        }
      >
        {t.erreurAccesAide}
      </EtatVide>
    </section>
  )
}

/** 401 après le réessai : la session est finie. On se reconnecte, et on revient exactement ici. */
export function SessionExpiree() {
  const t = useTextes()
  const { pathname, search } = useLocation()

  return (
    <section className="erreur">
      <EtatVide
        icone="utilisateur"
        titre={t.erreurSessionTitre}
        action={
          <LienBouton to={`/connexion?retour=${encodeURIComponent(pathname + search)}`}>
            {t.seConnecter}
          </LienBouton>
        }
      >
        {t.erreurSessionAide}
      </EtatVide>
    </section>
  )
}

/**
 * Le service ne répond pas — réseau coupé, 5xx, frontal sans API derrière.
 *
 * « Ce n'est pas de votre fait » est la phrase qui compte. « Réessayer » relance l'appel qui a
 * échoué, pas la page : ce qu'on avait à l'écran reste. Hors ligne, l'écran le dit autrement et
 * réessaie de lui-même au retour du réseau — le bouton reste, pour qui n'attend pas.
 *
 * Une cause 4xx qui n'a pas d'écran à elle (409, 429…) passe aussi par ici, avec sa phrase du
 * catalogue à la place de l'explication générique : « trop de tentatives » n'est pas une panne.
 */
export function ServiceIndisponible({
  cause,
  onReessayer,
  sortie = true,
}: {
  cause?: unknown
  onReessayer?: () => void
  /** La sortie « revenir à la recherche ». Faux au démarrage, où rien d'autre ne s'affiche. */
  sortie?: boolean
}) {
  const t = useTextes()
  const langue = useLangue()
  const enLigne = useEnLigne(onReessayer)
  const statut = estErreurApi(cause) ? cause.statut : 0
  const precision = statut >= 400 && statut < 500 ? phraseDErreur(langue, cause) : null

  return (
    <section className="erreur erreur--panne">
      <EtatVide
        icone={enLigne ? 'alerte' : 'wifi'}
        titre={enLigne ? t.erreurPanneTitre : t.erreurHorsLigneTitre}
        action={
          <div className="rangee rangee--serree">
            {onReessayer !== undefined && (
              <Bouton onClick={onReessayer}>
                <Icone nom="rafraichir" taille="sm" />
                {t.erreurReessayer}
              </Bouton>
            )}
            {sortie && (
              <LienBouton to="/recherche" apparence="secondaire">
                {t.revenirRecherche}
              </LienBouton>
            )}
          </div>
        }
      >
        {enLigne ? (precision ?? t.erreurPanneAide) : t.erreurHorsLigneAide}
      </EtatVide>
    </section>
  )
}

/** Le réseau, tel que le navigateur le voit ; et le geste à refaire quand il revient. */
function useEnLigne(auRetour?: () => void): boolean {
  const [enLigne, setEnLigne] = useState(() => globalThis.navigator?.onLine ?? true)

  useEffect(() => {
    const revenu = () => {
      setEnLigne(true)
      auRetour?.()
    }
    const parti = () => setEnLigne(false)

    window.addEventListener('online', revenu)
    window.addEventListener('offline', parti)

    return () => {
      window.removeEventListener('online', revenu)
      window.removeEventListener('offline', parti)
    }
  }, [auRetour])

  return enLigne
}

/** Une erreur de rendu attrapée : la page est cassée, l'application ne l'est pas. */
export function PageCassee() {
  const t = useTextes()

  return (
    <section className="erreur erreur--panne">
      <EtatVide
        icone="alerte"
        titre={t.erreurCasseeTitre}
        action={
          <div className="rangee rangee--serree">
            <Bouton onClick={() => window.location.reload()}>
              <Icone nom="rafraichir" taille="sm" />
              {t.erreurRecharger}
            </Bouton>
            <LienBouton to="/recherche" apparence="secondaire">
              {t.revenirRecherche}
            </LienBouton>
          </div>
        }
      >
        {t.erreurCasseeAide}
      </EtatVide>
    </section>
  )
}

/**
 * La limite d'erreur — autour des écrans, jamais autour de l'en-tête.
 *
 * Sans elle, une exception de rendu démonte tout l'arbre React : page blanche, sans menu, sans
 * moyen de partir ailleurs. Avec elle, l'écran cassé est remplacé par un état qui le dit, et
 * l'en-tête reste. `cle` — le chemin courant — réarme la limite : changer de page est une sortie,
 * elle doit fonctionner sans recharger. Un composant de classe, parce que React n'offre pas
 * `getDerivedStateFromError` autrement.
 */
export class LimiteErreur extends Component<{ cle: string; children: ReactNode }, { cassee: boolean }> {
  state = { cassee: false }

  static getDerivedStateFromError(): { cassee: boolean } {
    return { cassee: true }
  }

  componentDidCatch(erreur: unknown, info: ErrorInfo): void {
    console.error('Écran cassé :', erreur, info.componentStack)
  }

  componentDidUpdate(precedentes: { cle: string }): void {
    if (this.state.cassee && precedentes.cle !== this.props.cle) {
      this.setState({ cassee: false })
    }
  }

  render(): ReactNode {
    return this.state.cassee ? <PageCassee /> : this.props.children
  }
}
